import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Play } from 'lucide-react';
import type { PlaybackProvider, PlayerEvent } from '@mlm/shared';
import { cn } from '../lib/cn';
import type { PlaybackAdapter, PlayerSnapshot } from './adapter';
import { ControlBar } from './ControlBar';
import { PlayerNotice } from './PlayerNotice';
import {
  enterFullscreen,
  exitFullscreen,
  PSEUDO_FULLSCREEN_ATTR,
  type FullscreenMode,
} from './fullscreen';
import { useAutoHide, useFlash, usePlayer } from './usePlayer';

export type PlayerCaption = { id: string; lang: string; label: string; blobUrl: string };

export type PlayerSource =
  | { kind: 'video'; src: string; expiresInSeconds?: number; captions: PlayerCaption[] }
  | { kind: 'iframe'; src: string; provider: PlaybackProvider };

/** Imperative handles the host cannot reach through the adapter alone. */
export type PlayerControls = {
  toggleFullscreen: () => void;
  toggleCaptions: () => void;
  flash: (message: string) => void;
};

export function VideoPlayer({
  source,
  title,
  protocolId,
  chrome,
  preferences,
  onEvent,
  onAdapter,
  overlay,
  showBigPlay,
  onBookmark,
  onRateChange,
  onCaptionsChange,
  notice,
  className,
  seekLimit,
}: {
  source: PlayerSource;
  title: string;
  /** Stable per lesson — keys the adapter's lifetime, unlike `source.src`. */
  protocolId: string;
  chrome: 'native' | 'embed-strip' | 'none';
  preferences: { rate: number; muted?: boolean; volume?: number; captionsLang: string | null };
  onEvent?: (event: PlayerEvent, snapshot: PlayerSnapshot) => void;
  onAdapter?: (adapter: PlaybackAdapter | null, controls: PlayerControls | null) => void;
  overlay?: ReactNode;
  showBigPlay?: boolean;
  onBookmark?: () => void;
  /** Fired after the backend ACCEPTS a rate, so a refusal is never persisted. */
  onRateChange?: (rate: number) => void;
  onCaptionsChange?: (lang: string | null) => void;
  notice?: string | null;
  className?: string;
  /**
   * Furthest second the learner may seek to (`lms_seek_restriction`), or null
   * when unrestricted. The ±10s buttons and the scrubber both respect it; the
   * host still clamps programmatic seeks, since those never pass through here.
   */
  seekLimit?: number | null;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [fullscreenMode, setFullscreenMode] = useState<FullscreenMode | null>(null);
  const { message: flashed, flash } = useFlash();
  const { videoRef, iframeRef, adapter, snapshot } = usePlayer({
    kind: source.kind === 'video' ? 'video' : 'iframe',
    provider: source.kind === 'iframe' ? source.provider : 'file',
    src: source.src,
    protocolId,
    onEvent,
  });
  const { visible, show, toggle } = useAutoHide(snapshot.state === 'playing');

  // The `<video>` src is assigned IMPERATIVELY, never as a prop and never via a
  // `key` remount: React writing a new src attribute reloads the element and
  // yanks the learner back to 0:00. On a mid-lesson re-presign we carry the
  // LIVE currentTime across the swap — never the (stale) server position.
  const src = source.src;
  const assignedForRef = useRef<string | null>(null);
  useEffect(() => {
    const video = videoRef.current;
    if (source.kind !== 'video' || !video) return;
    if (video.src === src) return;
    // Carry the playhead only across a swap WITHIN the same lesson. Reusing
    // this element for a different lesson must start at 0 — the host already
    // unmounts between lessons, and this makes that non-negotiable rather than
    // a coincidence of render order.
    const sameLesson = assignedForRef.current === protocolId;
    assignedForRef.current = protocolId;
    const resumeAt = sameLesson ? video.currentTime : 0;
    const wasPlaying = sameLesson && !video.paused && !video.ended;
    video.src = src;
    const onMeta = () => {
      if (resumeAt > 0) video.currentTime = resumeAt;
      if (wasPlaying) void video.play().catch(() => {});
    };
    video.addEventListener('loadedmetadata', onMeta, { once: true });
    return () => video.removeEventListener('loadedmetadata', onMeta);
  }, [src, source.kind, protocolId, videoRef]);

  // Memoised: a fresh `[]` each render would re-key every dependent effect and
  // callback, and one of those owns the caption <track> modes.
  const captions = useMemo(() => (source.kind === 'video' ? source.captions : []), [source]);
  const captionsLang = preferences.captionsLang;

  // Apply the learner's per-device preferences once the backend can accept them.
  const ready = snapshot.status === 'ready';
  useEffect(() => {
    if (!adapter || !ready) return;
    void adapter.setRate(preferences.rate);
  }, [adapter, ready, preferences.rate]);
  useEffect(() => {
    if (!adapter || !ready || source.kind !== 'video') return;
    adapter.setCaptions(captionsLang);
  }, [adapter, ready, captionsLang, source.kind, captions]);

  const toggleFullscreen = useCallback(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    if (fullscreenMode) {
      void exitFullscreen(wrapper, fullscreenMode);
      setFullscreenMode(null);
      return;
    }
    const mode = adapter?.capabilities.fullscreen;
    void enterFullscreen(
      wrapper,
      videoRef.current,
      mode === 'none' || !mode ? 'element' : mode,
    ).then(setFullscreenMode);
  }, [adapter, fullscreenMode, videoRef]);

  const toggleCaptions = useCallback(() => {
    if (!adapter) return;
    const next = captionsLang ? null : (captions[0]?.lang ?? null);
    adapter.setCaptions(next);
    onCaptionsChange?.(next);
    flash(next ? 'Captions on' : 'Captions off');
  }, [adapter, captions, captionsLang, flash, onCaptionsChange]);

  // Escape and the Android back button leave the CSS pseudo-fullscreen; the
  // real one is unwound by the browser, so only mirror its state here.
  useEffect(() => {
    if (!fullscreenMode) return;
    const onFsChange = () => {
      if (fullscreenMode === 'element' && !document.fullscreenElement) setFullscreenMode(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && fullscreenMode === 'css' && wrapperRef.current) {
        e.stopPropagation();
        wrapperRef.current.removeAttribute(PSEUDO_FULLSCREEN_ATTR);
        setFullscreenMode(null);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [fullscreenMode]);

  const controlsRef = useRef<PlayerControls>({ toggleFullscreen, toggleCaptions, flash });
  controlsRef.current = { toggleFullscreen, toggleCaptions, flash };
  const onAdapterRef = useRef(onAdapter);
  onAdapterRef.current = onAdapter;
  useEffect(() => {
    onAdapterRef.current?.(adapter, adapter ? controlsRef.current : null);
    return () => onAdapterRef.current?.(null, null);
  }, [adapter]);

  // Shown before `ready` too: on iOS cellular / Low Power Mode `preload` is
  // ignored, so `loadedmetadata` never fires and the ONLY way to start the
  // lesson is a tap that calls play() inside the gesture.
  const bigPlay =
    !!showBigPlay || (source.kind === 'video' && snapshot.state === 'unstarted' && !ready);

  return (
    <div
      ref={wrapperRef}
      // Lets a page-level keydown listener tell "focus is in the player" from
      // "focus is somewhere else on the page" without threading a ref across
      // package boundaries.
      data-player-root=""
      className={cn(
        'relative h-full w-full overflow-hidden bg-black',
        // The pseudo-fullscreen fallback for WebViews that cancel the real one.
        '[&[data-pseudo-fullscreen]]:fixed [&[data-pseudo-fullscreen]]:inset-0 [&[data-pseudo-fullscreen]]:z-50 [&[data-pseudo-fullscreen]]:h-screen [&[data-pseudo-fullscreen]]:w-screen',
        className,
      )}
      onPointerDown={(e) => {
        if (e.pointerType === 'touch') toggle();
        else show();
      }}
      onPointerMove={(e) => e.pointerType !== 'touch' && show()}
    >
      {source.kind === 'video' ? (
        <video
          ref={videoRef}
          // No `controls`: the app-owned bar replaces them (and iOS hides the
          // native rate control entirely). No `crossOrigin`: caption tracks are
          // same-origin blob: URLs, so the media fetch stays a plain no-cors
          // request and never depends on the R2 bucket's CORS policy.
          playsInline
          preload="metadata"
          className="h-full w-full bg-black"
          title={title}
        >
          {captions.map((caption) => (
            <track
              key={caption.id}
              kind="captions"
              srcLang={caption.lang}
              label={caption.label}
              src={caption.blobUrl}
            />
          ))}
        </video>
      ) : (
        <iframe
          key={source.src}
          ref={iframeRef}
          src={source.src}
          title={title}
          className="h-full w-full bg-black"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
          // The provider keeps its OWN origin under `allow-same-origin` (it is
          // cross-origin to us), so the postMessage handshakes still work while
          // a passthrough URL can never script OUR page. Do not tighten this.
          sandbox="allow-scripts allow-same-origin allow-presentation allow-popups"
          referrerPolicy="strict-origin-when-cross-origin"
        />
      )}

      {bigPlay && adapter && (
        <button
          type="button"
          aria-label="Play"
          title="Play"
          onClick={() => void adapter.play()}
          className="absolute inset-0 flex items-center justify-center focus-visible:outline-none"
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/60 text-white ring-1 ring-white/25">
            <Play className="ml-1 h-7 w-7" />
          </span>
        </button>
      )}

      {chrome === 'native' && adapter && (
        <ControlBar
          snapshot={snapshot}
          adapter={adapter}
          visible={visible || snapshot.state !== 'playing'}
          fullscreen={!!fullscreenMode}
          onToggleFullscreen={toggleFullscreen}
          onBookmark={onBookmark}
          seekLimit={seekLimit}
          onSeekBlocked={() => flash('Watch to here first')}
          onSeekPreview={(s) => adapter.seek(s, { preview: true })}
          onSeekCommit={(s) => adapter.seek(s)}
          onRateChange={(rate) => {
            void adapter.setRate(rate).then((accepted) => {
              if (!accepted) return;
              onRateChange?.(rate);
              flash(rate === 1 ? 'Normal speed' : `${rate}× speed`);
            });
          }}
          onToggleCaptions={captions.length > 0 ? toggleCaptions : undefined}
        />
      )}

      <PlayerNotice message={notice ?? flashed} />
      {overlay}
    </div>
  );
}
